<script>
import { cloneDeep } from "lodash";
import { required } from "vuelidate/lib/validators";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import MaskedInput from "vue-text-mask";
import ItemCategory from "@/components/modals/item-category.vue";
import Unit from "@/components/modals/unit.vue";

export default {
  name: "Stocks",
  components: {
    MaskedInput,
    ItemCategory,
    Unit
  },
  data() {
    return {
      currencyMask: createNumberMask({
        prefix: "",
        allowDecimal: true
      }),
      quantityMask: createNumberMask({
        prefix: "",
        includeThousandsSeparator: false
      }),
      submitted: false,
      tableData: [],
      modalTitle: "",
      currentPage: 1,
      perPage: 10,
      filter: "",
      filterOn: [],
      sortBy: "id",
      sortDesc: false,
      fields: [
        {
          key: "name",
          sortable: true,
          label: "Stocks"
        }
      ],
      form: {
        campusId: null,
        id: null,
        stockNumber: "",
        name: "",
        description: "",
        unit: "",
        itemCategoryId: "",
        unitCost: "",
        iarItemId: 0,
        subsidiaryLedgerId: 0
      },
      tableBusy: false,
      editMode: false,
      itemCategory: "",
      campusId: this.$store.state.data.selectedCampus.id,
      uploadOptions: {
        type: "Property",
        placement: "lefttop",
        folder: "Property"
      },
      itemIndex: -1,
      selectOnly: false
    };
  },
  validations: {
    form: {
      stockNumber: { required },
      name: { required },
      unit: { required },
      itemCategoryId: { required }
    }
  },
  computed: {
    filtered() {
      let array = this.tableData;
      let keyword = this.filter;
      const regExp = new RegExp(keyword, "gi");
      const check = (obj) => {
        if (obj !== null && typeof obj === "object") {
          return Object.values(obj).some(check);
        }
        if (Array.isArray(obj)) {
          return obj.some(check);
        }
        return (
          (typeof obj === "string" || typeof obj === "number") &&
          regExp.test(obj)
        );
      };
      return array.filter(check);
    },
    rows() {
      if (this.filter.trim() != "" && this.filter) {
        return this.filtered.length;
      } else {
        return this.tableData.length;
      }
    }
  },
  methods: {
    getData(selectOnly) {
      if (selectOnly) this.selectOnly = true;
      this.campusId = this.$store.state.data.selectedCampus.id;
      this.tableBusy = true;
      this.$store
        .dispatch("stock/GetStocks", this.campusId)
        .then((res) => {
          res.data.forEach((item) => {
            item.visible = false;
            item.showDetails = false;
            item.showTransaction = false;
            item.transactionLoading = false;
            item.transactions = [];
          });
          this.tableData = res.data;
          this.tableBusy = false;
        })
        .catch(() => {
          this.tableBusy = false;
          this.showToast("Something went wrong - Getting stock list!", "error");
        });
    },
    onCreate() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      } else {
        var data = cloneDeep(this.form);
        data.stockNumber = data.stockNumber.trim();
        data.iarItemId = 0;
        data.unitCost = this.getExactAmt(data.unitCost);
        this.$store
          .dispatch("stock/CreateStock", data)
          .then((res) => {
            if (res.data.error) {
              return this.showToast(res.data.errorMessage, "error");
            }
            res.data.visible = false;
            res.data.showDetails = false;
            res.data.showTransaction = false;
            res.data.transactionLoading = false;
            res.data.transactions = [];
            this.tableData.push(res.data);
            this.showToast("Successfully created!", "success");
            this.onReset();
            this.$bvModal.hide("modal-create");
          })
          .catch((err) => {
            this.showToast("Something went wrong - Creating stock!", "error");
          });
        this.submitted = false;
      }
    },
    addStock() {
      this.onReset();
      this.form.campusId = this.campusId;
      this.modalTitle = "Create Stock";
      this.$bvModal.show("modal-create");
    },
    onReset() {
      this.form = {
        campusId: null,
        id: null,
        stockNumber: "",
        name: "",
        description: "",
        unit: "",
        itemCategoryId: "",
        unitCost: "",
        iarItemId: 0,
        subsidiaryLedgerId: 0
      };
      this.itemCategory = "";
      this.itemIndex = -1;
      this.submitted = false;
    },
    getItemCategory() {
      this.$refs.itemcategory.filterStockCategory();
      this.$bvModal.show("item-category-modal");
    },
    dropItemCat(data) {
      this.form.itemCategoryId = data.id;
      this.itemCategory = data.name;
    },
    getUnit() {
      this.$refs.unit.getData();
      this.$bvModal.show("unit-modal");
    },
    dropUnit(data) {
      this.form.unit = data.unitName;
    },
    onRowSelected(data) {
      this.$emit("dropData", data[0]);
      this.$bvModal.hide("stocks-modal");
    }
  }
};
</script>

<template>
  <b-modal
    id="stocks-modal"
    title="Stock List"
    size="lg"
    title-class="font-18"
    hide-footer
    no-close-on-backdrop
  >
    <!-- Create Stock -->
    <b-modal
      id="modal-create"
      :title="modalTitle"
      size="md"
      title-class="font-18"
      hide-footer
      no-close-on-backdrop
    >
      <form @submit.prevent="onCreate" class="needs-validation" ref="fcForm">
        <div class="mb-3">
          <label for="stockNumber">Stock Number </label>
          <input
            id="stockNumber"
            type="text"
            v-model="form.stockNumber"
            placeholder="Enter Stock Number..."
            class="form-control"
            :class="{
              'is-invalid': submitted && $v.form.stockNumber.$error
            }"
            autocomplete="off"
          />
          <div
            v-if="submitted && $v.form.stockNumber.$error"
            class="invalid-feedback"
          >
            <span v-if="!$v.form.stockNumber.required"
              >This value is required.</span
            >
          </div>
        </div>
        <div class="mb-3">
          <label for="stockName">Stock Name </label>
          <input
            id="stockName"
            type="text"
            v-model="form.name"
            placeholder="Enter Stock Name..."
            class="form-control"
            :class="{
              'is-invalid': submitted && $v.form.name.$error
            }"
            autocomplete="off"
          />
          <div v-if="submitted && $v.form.name.$error" class="invalid-feedback">
            <span v-if="!$v.form.name.required">This value is required.</span>
          </div>
        </div>
        <div class="mb-3">
          <label for="unitCost">Unit Price </label>
          <masked-input
            id="unitCost"
            type="text"
            v-model="form.unitCost"
            placeholder="Enter Unit Price..."
            class="form-control"
            :class="{
              'is-invalid': submitted && $v.form.unitCost.$error
            }"
            autocomplete="off"
            :mask="currencyMask"
          ></masked-input>
          <div
            v-if="submitted && $v.form.unitCost.$error"
            class="invalid-feedback"
          >
            <span v-if="!$v.form.unitCost.required"
              >This value is required.</span
            >
          </div>
        </div>
        <div class="mb-3">
          <label for="unit">Unit </label>
          <b-input-group>
            <template #append>
              <b-button @click="getUnit()" variant="outline-info"
                ><i class="mdi mdi-clipboard-text-search-outline"></i
              ></b-button>
            </template>
            <input
              autocomplete="off"
              type="text"
              disabled
              placeholder="Search Unit..."
              class="form-control"
              :class="{
                'is-invalid': submitted && $v.form.unit.$error
              }"
              v-model="form.unit"
            />
          </b-input-group>
          <div v-if="submitted && $v.form.unit.$error" class="invalid-feedback">
            <span v-if="!$v.form.unit.required">This value is required.</span>
          </div>
        </div>
        <div class="mb-3">
          <label for="itemCategoryId">Item Category </label>
          <b-input-group>
            <template #append>
              <b-button @click="getItemCategory" variant="outline-info"
                ><i class="mdi mdi-clipboard-text-search-outline"></i
              ></b-button>
            </template>
            <input
              autocomplete="off"
              type="text"
              disabled
              placeholder="Search Item Category..."
              class="form-control"
              :class="{
                'is-invalid': submitted && $v.form.itemCategoryId.$error
              }"
              v-model="itemCategory"
            />
          </b-input-group>
          <div
            v-if="submitted && $v.form.itemCategoryId.$error"
            class="invalid-feedback"
          >
            <span v-if="!$v.form.itemCategoryId.required"
              >This value is required.</span
            >
          </div>
        </div>
        <div class="mb-3">
          <label for="description">Stock Description </label>
          <textarea
            id="description"
            rows="6"
            v-model="form.description"
            placeholder="Enter Stock Description..."
            class="form-control"
            autocomplete="off"
          ></textarea>
        </div>

        <div class="d-flex flex-row-reverse mb-3">
          <button class="btn btn-primary mt-3" type="submit">
            Create New Stock
          </button>
        </div>
      </form>
    </b-modal>
    <!-- Main -->
    <div class="row">
      <div class="col-md-6">
        <div id="tickets-table_filter" class="dataTables_filter me-1">
          <label class="d-inline-flex align-items-center">
            <b-form-input
              autocomplete="off"
              v-model="filter"
              type="search"
              placeholder="Search Stocks..."
              class="form-control form-control-sm"
            ></b-form-input>
          </label>
        </div>
      </div>
      <div class="col-md-6">
        <div v-if="!selectOnly" class="d-flex align-items-center float-end">
          <button
            type="button"
            @click="addStock()"
            class="btn btn-sm btn-primary d-flex align-items-center ms-1"
          >
            <i class="bx bx-plus fs-6 me-1"></i>Create Stock
          </button>
        </div>
      </div>
    </div>
    <div class="table-responsive mb-0 mt-2">
      <b-table
        class="datatables target-table"
        :items="filtered"
        :fields="fields"
        responsive="sm"
        :per-page="perPage"
        :current-page="currentPage"
        sort-by="name"
        :sort-desc="false"
        :busy="tableBusy"
        bordered
        outlined
        hover
        striped
        show-empty
        select-mode="single"
        selectable
        @row-selected="onRowSelected"
        selected-variant="success"
      >
        <template #empty>
          <b-card-header>
            <div class="text-center">
              <div v-if="filter != ''">
                <i class="fas fa-exclamation-triangle me-2"></i>There are no
                stocks matching your request
              </div>
              <div v-else>
                <i class="fas fa-exclamation-triangle me-2"></i>No stocks
                available
              </div>
            </div>
          </b-card-header>
        </template>
        <template #table-busy>
          <b-card-header>
            <div class="d-flex justify-content-center align-items-center">
              <div class="preloader-component me-2">
                <div class="status">
                  <div class="spinner-chase w-20px h-20px">
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                    <div class="chase-dot"></div>
                  </div>
                </div>
              </div>
              <strong>Loading...</strong>
            </div>
          </b-card-header>
        </template>
        <template #cell(name)="row">
          <div class="d-flex align-items-center pe-5">
            <div class="text-muted">
              <h5 class="mb-0">
                {{ row.item.name }}
              </h5>
              <div>
                <small class="d-flex align-items-center"
                  >{{
                    row.item.stockNumber &&
                    row.item.stockNumber != "Not Indicated"
                      ? `Stock# ${row.item.stockNumber}`
                      : "Not Indicated"
                  }}
                </small>
              </div>
            </div>
          </div>
        </template>
      </b-table>
    </div>
    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-end">
          <ul class="pagination pagination-rounded mb-0" style="z-index: -1">
            <!-- pagination -->
            <b-pagination
              v-model="currentPage"
              :total-rows="rows"
              :per-page="perPage"
            ></b-pagination>
          </ul>
        </div>
      </div>
    </div>
    <item-category
      ref="itemcategory"
      @dropData="dropItemCat($event)"
    ></item-category>
    <unit ref="unit" @dropData="dropUnit($event)"></unit>
  </b-modal>
</template>
